import { useState, useEffect, type ReactNode, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, Briefcase, CreditCard, ShieldCheck,
  Settings, Bell, ChevronLeft, ChevronRight, ChevronDown, Menu, X,
  BadgeCheck, LogOut, Package, Tag, ShoppingBag, MessageSquare, DollarSign,
  Megaphone, Sparkles, Calendar, Ticket, FileText, TrendingUp, Coins,
  Rocket, Handshake, Heart, Leaf, LineChart, ListOrdered, ArrowUpCircle,
  PlusCircle, UsersRound, Receipt, Inbox, Link2, ClipboardList, Trophy,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutContextProvider, useLayoutCtx, useLayoutSync } from "./LayoutContext";

type IconCmp = ComponentType<{ size?: number; className?: string }>;
export type LeafItem = { key: string; label: string; icon: IconCmp };
export type GroupItem = { groupKey: string; label: string; icon: IconCmp; children: LeafItem[] };
export type SidebarEntry = LeafItem | GroupItem;

export const isGroup = (e: SidebarEntry): e is GroupItem => "children" in e;

export const sidebarLeafKeys = (items: SidebarEntry[] = adminSidebarItems): string[] =>
  items.flatMap(e => isGroup(e) ? e.children.map(c => c.key) : [e.key]);

export const adminSidebarItems: SidebarEntry[] = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "members", label: "Members", icon: BadgeCheck },
  { key: "merchants", label: "Merchants", icon: ShoppingBag },
  { key: "verifications", label: "Verifications", icon: BadgeCheck },
  { key: "member-interests", label: "Member Interest", icon: ArrowUpCircle },
  { key: "products", label: "Products", icon: Package },
  { key: "services", label: "Services", icon: Briefcase },
  { key: "events", label: "Events", icon: Calendar },
  { key: "event-orders", label: "Ticket Sales", icon: Ticket },
  { key: "leads", label: "Leads", icon: MessageSquare },
  { key: "inquiries", label: "Inquiries", icon: FileText },
  { key: "sales", label: "Sales", icon: DollarSign },
  {
    groupKey: "investments",
    label: "Investments",
    icon: LineChart,
    children: [
      { key: "investors", label: "Investors", icon: TrendingUp },
      { key: "opportunities", label: "Opportunities", icon: Coins },
      { key: "listings", label: "Startup Listings", icon: Rocket },
      { key: "interests", label: "Interests Queue", icon: Heart },
      { key: "deal-rooms", label: "Deal Rooms", icon: Handshake },
    ],
  },
  {
    groupKey: "crowdfunding",
    label: "Crowdfunding",
    icon: Heart,
    children: [
      { key: "crowdfunding", label: "Campaigns", icon: Heart },
      { key: "fundraise-requests", label: "Requests", icon: Inbox },
      { key: "crowdfunders", label: "Crowdfunders", icon: UsersRound },
      { key: "crowdfunding-transactions", label: "Contributions", icon: Receipt },
      { key: "member-access", label: "Member Access", icon: ShieldCheck },
    ],
  },
  {
    groupKey: "awards-group",
    label: "Awards",
    icon: Trophy,
    children: [
      { key: "awards", label: "Awards Setup", icon: Trophy },
      { key: "award-contestants", label: "Contestants", icon: UsersRound },
      { key: "award-nominations", label: "Nominations", icon: ClipboardList },
      { key: "award-votes", label: "Vote Transactions", icon: Receipt },
      { key: "award-payment-logs", label: "Payment Logs", icon: ListOrdered },
    ],
  },
  {
    groupKey: "affiliates-group",
    label: "Affiliates",
    icon: Link2,
    children: [
      { key: "affiliates", label: "Affiliates", icon: UsersRound },
      { key: "affiliate-registrations", label: "Registrations", icon: ClipboardList },
    ],
  },
  { key: "esg-inquiries", label: "ESG Inquiries", icon: Leaf },
  { key: "ads", label: "Ad Packages", icon: Megaphone },
  { key: "promotions", label: "Promotions", icon: Sparkles },
  { key: "packages", label: "Packages", icon: Tag },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "payment-logs", label: "Payment Logs", icon: ListOrdered },
  { key: "settings", label: "Settings", icon: Settings },
];

type AdminLayoutProps = {
  children: ReactNode;
  activeSection?: string;
  title?: string;
};

export function AdminLayoutChrome({ children }: { children: ReactNode }) {
  const ctx = useLayoutCtx();
  const activeSection = ctx.activeSection || "overview";
  const title = ctx.title || "Admin Panel";
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const groupHasActive = (g: GroupItem) => g.children.some(c => c.key === activeSection);
  const initialOpen = () => {
    const out: Record<string, boolean> = {};
    for (const e of adminSidebarItems) {
      if (isGroup(e)) out[e.groupKey] = groupHasActive(e);
    }
    return out;
  };
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpen);

  // If the active section changes to a child, ensure its parent group is open.
  useEffect(() => {
    setOpenGroups(prev => {
      const next = { ...prev };
      for (const e of adminSidebarItems) {
        if (isGroup(e) && groupHasActive(e)) next[e.groupKey] = true;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const sidebarWidth = collapsed ? 72 : 240;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const goTo = (key: string) => {
    navigate(key === "overview" ? "/admin" : `/admin/${key}`);
    setMobileOpen(false);
  };

  const toggleGroup = (groupKey: string) => {
    if (collapsed) return;
    setOpenGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-4 h-16 border-b border-white/10 shrink-0">
        {!collapsed && <span className="text-white font-semibold text-sm whitespace-nowrap">Admin Panel</span>}
      </div>

      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {adminSidebarItems.map((entry) => {
          if (!isGroup(entry)) {
            return (
              <button
                key={entry.key}
                onClick={() => goTo(entry.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeSection === entry.key
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
                title={collapsed ? entry.label : undefined}
              >
                <entry.icon size={20} className="shrink-0" />
                {!collapsed && <span>{entry.label}</span>}
              </button>
            );
          }

          const isOpen = !collapsed && openGroups[entry.groupKey];
          const hasActiveChild = groupHasActive(entry);
          return (
            <div key={entry.groupKey}>
              <button
                onClick={() => collapsed ? goTo(entry.children[0].key) : toggleGroup(entry.groupKey)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  hasActiveChild
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
                title={collapsed ? entry.label : undefined}
              >
                <entry.icon size={20} className="shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{entry.label}</span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </>
                )}
              </button>
              {isOpen && (
                <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
                  {entry.children.map(child => (
                    <button
                      key={child.key}
                      onClick={() => goTo(child.key)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeSection === child.key
                          ? "bg-white/10 text-white"
                          : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <child.icon size={16} className="shrink-0" />
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-3 space-y-1 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">{authUser?.name?.charAt(0) || "A"}</span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{authUser?.name || "Admin"}</p>
              <p className="text-white/40 text-xs truncate">{authUser?.email}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-red-400 hover:bg-white/5 transition-colors"
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          {collapsed ? <ChevronRight size={18} className="shrink-0" /> : <><ChevronLeft size={18} className="shrink-0" /><span>Collapse</span></>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <motion.aside
        animate={{ width: sidebarWidth }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="hidden lg:flex flex-col bg-navy border-r border-white/10 shrink-0 overflow-hidden"
      >
        <SidebarContent />
      </motion.aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-navy border-r border-white/10 z-50 flex flex-col"
            >
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 shrink-0 bg-white">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-900">
              <Menu size={22} />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative text-gray-500 hover:text-gray-900 p-2">
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold text-xs font-bold">{authUser?.name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "A"}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">{authUser?.name || "Admin"}</p>
                <p className="text-xs text-gray-500 capitalize">{authUser?.role}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

function AdminLayoutTitleSync({ title, activeSection, children }: { title?: string; activeSection?: string; children: ReactNode }) {
  useLayoutSync(title, activeSection);
  return <>{children}</>;
}

export default function AdminLayout({ children, activeSection = "overview", title = "Admin Panel" }: AdminLayoutProps) {
  const ctx = useLayoutCtx();
  if (ctx.mounted) {
    return <AdminLayoutTitleSync title={title} activeSection={activeSection}>{children}</AdminLayoutTitleSync>;
  }
  return (
    <LayoutContextProvider defaultTitle={title} defaultActiveSection={activeSection}>
      <AdminLayoutChrome>{children}</AdminLayoutChrome>
    </LayoutContextProvider>
  );
}
